/**
 * CreatorProfileView — Iter-254
 *
 * Dedicated, opinionated profile view for verified Creator Network talent.
 * Differs from the generic /client/discover/:talentId page in that it
 * foregrounds creator-specific signal: intro reel, follower stats per
 * platform, content categories, audience locations, brand collaborations,
 * media kit and a "Book for Campaign" CTA.
 *
 * For talent that is NOT a verified creator we transparently fall back to
 * the standard `/client/discover/:talentId` page so legacy entry points
 * never deadend on a half-empty creator view.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Instagram, Music2, Youtube, Globe, FileText, ExternalLink, MapPin, Award, Star, BadgeCheck, Heart, MessageSquare, Camera } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../utils/api';
import FaceSafeImage from '../../components/FaceSafeImage';

const fmtCount = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return `${v}`;
};

const StatTile = ({ label, value, icon: Icon, testId }) => (
  <div className="rounded-xl bg-white border border-gray-200 px-3 py-3 sm:px-4 sm:py-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-gray-500 text-[11px] sm:text-xs uppercase tracking-wide">
      {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
      <span>{label}</span>
    </div>
    <div className="mt-1 text-lg sm:text-2xl font-semibold text-gray-900">{value}</div>
  </div>
);

const SocialChip = ({ icon: Icon, label, sub, href, testId }) => {
  const inner = (
    <div
      className="flex items-center gap-3 rounded-xl border border-purple-200 bg-white px-3 py-2 hover:border-purple-400 transition"
      data-testid={testId}
    >
      <Icon className="w-5 h-5 text-purple-600" />
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900 leading-tight">{label}</div>
        {sub ? <div className="text-[11px] text-gray-500 leading-tight">{sub}</div> : null}
      </div>
      {href ? <ExternalLink className="w-3.5 h-3.5 text-gray-400" /> : null}
    </div>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className="block no-underline">
        {inner}
      </a>
    );
  }
  return inner;
};

export default function CreatorProfileView() {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/discover/talent/${talentId}`);
        if (!mounted) return;
        setProfile(res.data);
      } catch (e) {
        if (!mounted) return;
        setError(e.response?.data?.detail || 'Failed to load creator profile');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [talentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="creator-profile-loading">
        <div className="text-gray-600 text-sm">Loading creator profile…</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 text-center" data-testid="creator-profile-error">
        <div className="text-gray-900 font-semibold">Creator profile unavailable</div>
        <div className="text-gray-500 text-sm mt-1">{error || 'This profile is not currently visible.'}</div>
        <Button className="mt-4" onClick={() => navigate(-1)} data-testid="creator-back-btn">Go back</Button>
      </div>
    );
  }

  // If the talent is NOT a verified creator, fall through to the generic
  // talent detail page. Keeps legacy entry points safe.
  if (!profile.is_influencer && !profile.is_content_creator) {
    return <Navigate to={`/client/discover/${talentId}`} replace />;
  }

  const creatorType = profile.creator_type || (
    profile.is_influencer && profile.is_content_creator
      ? 'both'
      : profile.is_influencer
        ? 'influencer'
        : 'content_creator'
  );
  const creatorLabel = creatorType === 'both' ? 'Influencer + Content Creator'
    : creatorType === 'influencer' ? 'Influencer'
      : 'Content Creator';

  const socialLinks = [];
  if (profile.instagram_handle) {
    socialLinks.push({
      icon: Instagram,
      label: `@${profile.instagram_handle}`,
      sub: 'Instagram',
      href: `https://instagram.com/${profile.instagram_handle}`,
      key: 'ig',
    });
  }
  if (profile.tiktok_handle) {
    socialLinks.push({
      icon: Music2,
      label: `@${profile.tiktok_handle}`,
      sub: 'TikTok',
      href: `https://tiktok.com/@${profile.tiktok_handle}`,
      key: 'tt',
    });
  }
  if (profile.youtube_channel) {
    socialLinks.push({
      icon: Youtube,
      label: profile.youtube_channel,
      sub: 'YouTube',
      href: profile.youtube_channel.startsWith('http') ? profile.youtube_channel : `https://youtube.com/${profile.youtube_channel}`,
      key: 'yt',
    });
  }
  if (profile.website_url) {
    socialLinks.push({
      icon: Globe,
      label: profile.website_url.replace(/^https?:\/\//, ''),
      sub: 'Website',
      href: profile.website_url.startsWith('http') ? profile.website_url : `https://${profile.website_url}`,
      key: 'web',
    });
  }

  const followerTotal = (Number(profile.follower_count) || 0) + (Number(profile.facebook_followers) || 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-white" data-testid="creator-profile-view">
      {/* Header bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="creator-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Badge className="bg-purple-100 text-purple-800 border-purple-200">
            Creator Network
          </Badge>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Hero */}
        <Card className="overflow-hidden border-purple-200">
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
              {/* Intro Reel / Headshot. Iter-297d — switched to a 3:4
                  portrait aspect on mobile/desktop and bumped the image
                  object-position so faces never get sliced. The previous
                  `aspect-square` crop chopped the top of heads on tall
                  portrait shots. */}
              <div
                className="md:col-span-2 bg-gray-900 aspect-[3/4] md:aspect-auto md:min-h-[360px] relative flex items-center justify-center overflow-hidden"
                data-testid="creator-reel-area"
              >
                {profile.intro_video_url ? (
                  <video
                    src={profile.intro_video_url}
                    controls
                    playsInline
                    poster={profile.profile_photo_url || undefined}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: '50% 25%' }}
                    data-testid="creator-intro-video"
                  />
                ) : profile.profile_photo_url ? (
                  <FaceSafeImage
                    src={profile.profile_photo_url}
                    alt={profile.display_name}
                    className="absolute inset-0 w-full h-full"
                    data-testid="creator-photo"
                  />
                ) : (
                  <div className="text-gray-500 text-sm">No media yet</div>
                )}
              </div>
              {/* Hero details */}
              <div className="md:col-span-3 p-5 sm:p-6 space-y-3">
                <div className="flex items-start gap-2 flex-wrap">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight" data-testid="creator-display-name">
                    {profile.display_name}
                  </h1>
                  {profile.verified_creator ? (
                    <Badge className="bg-purple-600 text-white" data-testid="creator-verified-badge">
                      <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Verified
                    </Badge>
                  ) : null}
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
                  <span className="inline-flex items-center gap-1" data-testid="creator-type-pill">
                    <Camera className="w-3.5 h-3.5" /> {creatorLabel}
                  </span>
                  {profile.city && profile.state ? (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> {profile.city}, {profile.state}
                    </span>
                  ) : null}
                  {profile.avg_rating ? (
                    <span className="inline-flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500" />
                      {Number(profile.avg_rating).toFixed(1)} · {profile.rating_count || 0} reviews
                    </span>
                  ) : null}
                </div>
                {profile.bio ? (
                  <p className="text-sm text-gray-700 leading-relaxed" data-testid="creator-bio">{profile.bio}</p>
                ) : null}
                <div className="pt-2 flex gap-2 flex-wrap">
                  <Button
                    onClick={() => toast.info('Book this creator from a gig — pick "Creator Network" in role selection.')}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="creator-book-cta"
                  >
                    Book for Campaign
                  </Button>
                  {profile.media_kit_url ? (
                    <a
                      href={profile.media_kit_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-800 hover:bg-gray-50"
                      data-testid="creator-media-kit-link"
                    >
                      <FileText className="w-4 h-4" /> Media Kit
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stat row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile
            label="Total Followers"
            value={fmtCount(followerTotal)}
            icon={Heart}
            testId="creator-stat-followers"
          />
          <StatTile
            label="Avg Views / Post"
            value={fmtCount(profile.average_views)}
            icon={MessageSquare}
            testId="creator-stat-avg-views"
          />
          <StatTile
            label="Engagement"
            value={profile.engagement_rate != null ? `${Number(profile.engagement_rate).toFixed(1)}%` : '—'}
            icon={Star}
            testId="creator-stat-engagement"
          />
          <StatTile
            label="Completed Gigs"
            value={`${profile.completed_gigs || 0}`}
            icon={Award}
            testId="creator-stat-completed-gigs"
          />
        </div>

        {/* Social channels */}
        {socialLinks.length > 0 ? (
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-3">
              <div className="text-sm font-semibold text-gray-900">Channels</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="creator-social-channels">
                {socialLinks.map((s) => (
                  <SocialChip
                    key={s.key}
                    icon={s.icon}
                    label={s.label}
                    sub={s.sub}
                    href={s.href}
                    testId={`creator-channel-${s.key}`}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Content categories + audience */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-2">
              <div className="text-sm font-semibold text-gray-900">Content Categories</div>
              <div className="flex flex-wrap gap-2" data-testid="creator-categories">
                {(profile.creator_categories || []).length > 0 ? (
                  profile.creator_categories.map((c) => (
                    <Badge key={c} className="bg-purple-100 text-purple-800 border-purple-200">{c}</Badge>
                  ))
                ) : (
                  <span className="text-sm text-gray-400">Not specified</span>
                )}
              </div>
              {(profile.content_types || []).length > 0 ? (
                <>
                  <div className="text-xs text-gray-500 mt-3">Formats</div>
                  <div className="flex flex-wrap gap-2" data-testid="creator-content-types">
                    {profile.content_types.map((c) => (
                      <Badge key={c} variant="outline" className="border-gray-300 text-gray-700">{c}</Badge>
                    ))}
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-2">
              <div className="text-sm font-semibold text-gray-900">Audience Locations</div>
              <div className="flex flex-wrap gap-2" data-testid="creator-audience">
                {(profile.audience_locations || []).length > 0 ? (
                  profile.audience_locations.map((c) => (
                    <Badge key={c} className="bg-blue-50 text-blue-800 border-blue-200">{c}</Badge>
                  ))
                ) : (
                  <span className="text-sm text-gray-400">Not specified</span>
                )}
              </div>
              {profile.influencer_tier ? (
                <div className="text-xs text-gray-500 mt-3">
                  Tier: <span className="text-gray-800 font-medium capitalize">{profile.influencer_tier}</span>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>

        {/* Brand collaborations */}
        {(profile.worked_with_brands || []).length > 0 ? (
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-3">
              <div className="text-sm font-semibold text-gray-900">Past Brand Collaborations</div>
              <div className="flex flex-wrap gap-3" data-testid="creator-brands">
                {profile.worked_with_brands.map((b, i) => (
                  <div
                    key={(b.brand_name || '') + i}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 bg-white"
                    data-testid={`creator-brand-${i}`}
                  >
                    {b.brand_logo_url ? (
                      <img src={b.brand_logo_url} alt={b.brand_name} className="w-6 h-6 rounded object-cover" />
                    ) : null}
                    <span className="text-sm font-medium text-gray-800">{b.brand_name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Portfolio */}
        {(profile.portfolio_images || []).length > 0 ? (
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-3">
              <div className="text-sm font-semibold text-gray-900">Portfolio</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="creator-portfolio">
                {profile.portfolio_images.map((src, i) => (
                  <FaceSafeImage
                    key={src + i}
                    src={src}
                    alt={`Portfolio ${i + 1}`}
                    className="w-full aspect-[4/5] rounded-lg border border-gray-200"
                    data-testid={`creator-portfolio-img-${i}`}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Footer fallback link */}
        <div className="text-center pt-2 pb-6">
          <button
            type="button"
            onClick={() => navigate(`/client/discover/${talentId}`)}
            className="text-xs text-gray-500 underline hover:text-gray-700"
            data-testid="creator-view-standard-profile"
          >
            View standard talent profile
          </button>
        </div>
      </div>
    </div>
  );
}
